/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { DateHelper } from "@src/app/shared/dateHelper";
import { ImportDoc, ImportDocNote } from "@src/app/modules/importer/ImportDoc";
import { ImportField } from "@src/app/modules/importer/ImportField";
import { ParseOptionBase, ParseValidator } from "@src/app/modules/importer/General/ParseOptionBase";
import { ParseOptionBaseMapped } from "@src/app/modules/importer/General/ParseOptionBaseMapped";

export class POBValidators {
    /**
     * requiredPOB messes up if there is more than one required column,
     * for now simple validator if selected, must have a value, but not forcing user to select...
     */
    static requiredPOB() {
        const pobValidator = new ParseValidator();

        pobValidator.validateRow = (field: ImportField) => {
            const val = field.getValue();
            if (val === undefined || val === null || val === '' || (typeof val === 'string' && val.trim() === '')) {
                return $localize` Value not provided for required field`
            } else {
                return null;
            }
        }

        return pobValidator;
    }

    static noNewLines() {
        const pobValidator = new ParseValidator();

        pobValidator.validateRow = (field: ImportField) => {
            const val = field.getValue();
            if (typeof val === 'string') {
                if (val.includes("\n") || val.includes("<br") || val.includes("<BR")) {
                    return $localize` Value should only contain one line of text`
                }
            }
            return null;
        }

        return pobValidator;
    }
}

export const requiredPOB = {
    prepareOption(pob: ParseOptionBase, importDoc: ImportDoc) {
        if (!pob.hasValidatorNote(this)) {
            const noteItem = new ImportDocNote({
                note: $localize`${pob.name} is required, you will need to select a column with values for it`,
                needsFix: true
            });
            pob.addValidatorNote(this, noteItem, importDoc)
        }
    }, validateRow: (field: ImportField) => {
        const val = field.getValue();
        if (val === undefined || val === null || val === '' || (typeof val === 'string' && val.trim() === '')) {
            return $localize` Value not provided for required field`
        } else {
            return null;
        }
    },
    optionSelected(pob: ParseOptionBase, importDoc: ImportDoc) {
        pob.removeValidatorNote(this, importDoc);
    }
} as ParseValidator;

export const requiredNumber = {
    validateRow: (field: ImportField) => {
        const val = field.getValue() as string;
        const THOUSANDS_SEP = ','; // Sorry Thomas - You will need to genericise for Europe...
        if (!isNaN(+val)) {
            field.setValue(Number(val));
            return null;
        }
        if (val.trim() === '0' || val.trim() === '0.00') {
            field.setValue(0);
            return null;
        }
        const val2 = val.replace(THOUSANDS_SEP, '');
        if (+val2 ) {
            field.setValue(+val2);
            return null;
        } else {
            return $localize` Value not provided for required number field`
        }
    }
} as ParseValidator

export const requiredOrDefaultMap = {
    prepareOption(pob: ParseOptionBase, importDoc: ImportDoc) {
        if (!pob.hasValidatorNote(this)) {
            const noteText = $localize`${pob.name} is required, either select a column with values for it OR delete this item and enter a default value for it`;

            const noteItem = {
                note: noteText, needsFix: true, deleteHandler: () => {
                    if (pob instanceof ParseOptionBaseMapped) {
                        pob.createOptionMapperInput('default_OPTION');
                        pob.setMappedTo('default_OPTION');
                    } else {
                        console.error('ParseOption does not support default mapping', { pob });
                    }
                }
            } as ImportDocNote
            pob.addValidatorNote(this, noteItem, importDoc)
        }
    }, validateRow: (field: ImportField) => {
        const val = field.getValue();
        if (val === undefined || val === null || val === '' || (typeof val === 'string' && val.trim() === '')) {
            return $localize` Value not provided for required field`
        } else {
            return null;
        }
    },
    optionSelected(pob: ParseOptionBase, importDoc: ImportDoc) {
        pob.removeValidatorNote(this, importDoc);
    }
} as ParseValidator;

export const validateEmailPOB = () => ({
    validateRow: (field: ImportField) => {
        const EMAIL_REGEXP = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-z0-9-]+(\.[a-z0-9-]+)*$/i;
        const value = field.getValue() as string;
        if (value) {
            if (!EMAIL_REGEXP.test(value)) {
                return $localize`'${value}' is not a valid email address`;
            }
        }
        return null;
    }
} as ParseValidator);

export const validateDatePOB = (allowEmpty = false) => ({
    validateRow: (field: ImportField) => {
        const dateIn = field.getValue() as string;
        if (allowEmpty && !dateIn) {
            return null;
        }
        const dv = new DateValidator(dateIn);
        if (dv.isValid()) {
            field.setValue(dv.getISODate())
            return null;
        }
        return $localize`${field.getValue()} is not a recognisable date`;
    }
} as ParseValidator);

class DateExaminer {
    constructor(private year: string, private month: string, private day: string) { }

    public isValid() {
        if (!isNaN(Date.parse(`${this.year}-${this.month}-${this.day} 00:00:00 GMT`))) {
            return true;
        }
        return false;
    }
}

class DateValidator {

    isYYYYMMDD(dateString: string) {
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        return regex.test(dateString);
    }

    isMMDDYYYYSlashed(dateString: string) {
        // Test it looks like 99/99/9999
        const regex = /^\d{2}\/\d{2}\/\d{4}$/;
        return regex.test(dateString);
    }

    isDDMMMYYYY(dateString: string) {
        // Test it looks like e.g. 01JAN2025 - 99XXX9999
        const regex = /^\d{2}[a-zA-Z]{3}\d{4}$/;
        return regex.test(dateString);
    }

    private format: string;
    private dateValue: string;
    private valid: boolean;

    constructor(private dateString: string) {
        if (this.isYYYYMMDD(dateString)) this.format = 'YYYY-MM-DD'
        if (this.isMMDDYYYYSlashed(dateString)) this.format = 'DD/MM/YYYY';
        if (this.isDDMMMYYYY(dateString)) this.format = 'DDMMMYYYY'

        this.checkDate();
    }

    public isValid() {
        return this.valid;
    }

    public getISODate() {
        return this.dateValue;
    }

    private checkDate() : void {
        if (!this.format) return;

        if (!this.dateString || typeof this.dateString !== 'string') {
            return;
        }
        if (this.format === 'YYYY-MM-DD') {
            const parts = this.dateString.split('-');
            if (parts.length === 3 && new DateExaminer(parts[0], parts[1], parts[2]).isValid()) {
                this.valid = true;
                this.dateValue = this.dateString;
            }
        } else if (this.format === 'DD/MM/YYYY') {
            const parts = this.dateString.split('/');
            if (parts.length === 3 && (new DateExaminer(parts[2], parts[1], parts[0]).isValid())) {
                this.valid = true;
                this.dateValue = DateHelper.isoFromDDMMYY(this.dateString);
            }
        } else if (this.format === 'DDMMMYYYY') {
            const val = DateHelper.isoFromDDMMMYYYY(this.dateString)
            if (val) {
                this.dateValue = val;
                this.valid = true;
            }
        }
    }
}
